import { useCallback, useEffect, useRef, useState } from "react";
import useWindowSize, {
  UseWindowResizeOptions,
} from "utils/hooks/useWindowResize";

export function getTruncateMultilineTextStyles(lineClamp: number) {
  return `
    display: -webkit-box;
    -webkit-line-clamp: ${lineClamp};
    -webkit-box-orient: vertical;
    overflow: hidden;
  `;
}

export interface UseMultilineEllipseOptions extends UseWindowResizeOptions {
  baseRem?: number;
  forceStateUpdateToggle?: boolean;
  lineClamp: number;
  lineHeight?: `${number}rem`;
}

export function useMultilineEllipse<ElementType extends HTMLElement>({
  baseRem = 16,
  forceStateUpdateToggle,
  lineClamp,
  lineHeight = "1.5rem",
  ...windowSizeOptions
}: UseMultilineEllipseOptions) {
  const { width } = useWindowSize(windowSizeOptions);

  const ref = useRef<ElementType | null>(null);
  const [showingEllipsis, setShowingEllipsis] = useState(false);
  const [isMinElementHeight, setIsElementMinHeight] = useState(false);

  const truncateMultilineTextCallback = useCallback(
    () => getTruncateMultilineTextStyles(lineClamp),
    [lineClamp]
  );
  const truncateMultilineTextCss = truncateMultilineTextCallback();

  useEffect(() => {
    const current = ref?.current;
    if (!current) {
      return;
    }

    const lineHeightMultiplier = Number(lineHeight?.split("rem")[0]);
    const lineHeightPixels = lineHeightMultiplier * baseRem;
    setIsElementMinHeight(current.clientHeight <= lineClamp * lineHeightPixels);

    setShowingEllipsis(current.offsetHeight < current.scrollHeight);
  }, [baseRem, forceStateUpdateToggle, lineClamp, lineHeight, ref, width]);

  return {
    isMinElementHeight,
    ref,
    showingEllipsis,
    truncateMultilineTextCss,
  };
}
